import { useCallback, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const CAPTURE_WIDTH = 600;
const SCORE_CHART_HEIGHT = 210;
const DOMAINS_CHART_HEIGHT = 190;
const SCORE_CHART_BOTTOM_GAP = 32;
const SECTION_GAP = 20;
const PAGE_PADDING_PX = 12;
const COMPACT_ROOT_PADDING_PX = 12;
const CLONE_BOTTOM_PADDING_PX = 20;

const getScale = () =>
  typeof window !== 'undefined' && 'ontouchstart' in window ? 1 : 2;

function getDomainsChartHeight(clonedRoot) {
  const chart = clonedRoot.querySelector('.print-right-section .print-pdf-chart');
  if (!chart) return DOMAINS_CHART_HEIGHT;
  const tickCount = chart.querySelectorAll(
    '.recharts-yAxis .recharts-cartesian-axis-tick'
  ).length;
  const count = tickCount > 0 ? tickCount : 4;
  return Math.min(240, Math.max(DOMAINS_CHART_HEIGHT, count * 44 + 72));
}

function applyPdfChromeVisibility(clonedRoot, { includeHeaderButtons }) {
  if (includeHeaderButtons) {
    clonedRoot.style.position = 'relative';
    clonedRoot.style.paddingRight = '52px';

    clonedRoot.querySelectorAll('.pdf-export-close, .pdf-export-print').forEach((el) => {
      el.style.setProperty('display', 'flex', 'important');
      el.style.visibility = 'visible';
      el.style.opacity = '1';
    });

    const closeBtn = clonedRoot.querySelector('.pdf-export-close');
    if (closeBtn) {
      closeBtn.style.position = 'absolute';
      closeBtn.style.top = '14px';
      closeBtn.style.right = '14px';
      closeBtn.style.zIndex = '20';
    }

    clonedRoot.querySelectorAll('.pdf-export-hide-on-pdf').forEach((el) => {
      el.style.display = 'none';
    });
    return;
  }

  const actionsEl = clonedRoot.querySelector('.proficiency-exam-header-actions');
  if (actionsEl) actionsEl.style.display = 'none';

  clonedRoot.querySelectorAll('.proficiency-exam-close-wrap').forEach((el) => {
    el.style.display = 'none';
  });

  clonedRoot
    .querySelectorAll('.proficiency-exam-flag-trigger .MuiSvgIcon-root')
    .forEach((el) => {
      el.style.display = 'none';
    });
}

function applyPdfCaptureToClone(clonedRoot, options = {}) {
  if (!clonedRoot) return;

  const { includeHeaderButtons = false } = options;

  clonedRoot.style.width = `${CAPTURE_WIDTH}px`;
  clonedRoot.style.minWidth = `${CAPTURE_WIDTH}px`;
  clonedRoot.style.maxWidth = 'none';
  clonedRoot.style.padding = `${COMPACT_ROOT_PADDING_PX}px`;
  clonedRoot.style.paddingBottom = `${COMPACT_ROOT_PADDING_PX + CLONE_BOTTOM_PADDING_PX}px`;
  clonedRoot.style.overflow = 'visible';
  clonedRoot.style.background = '#ffffff';
  clonedRoot.style.boxSizing = 'border-box';

  applyPdfChromeVisibility(clonedRoot, { includeHeaderButtons });

  const dragHeader = clonedRoot.querySelector('.proficiency-exam-drag-handle');
  if (dragHeader) {
    dragHeader.style.width = '100%';
    dragHeader.style.margin = '0';
    dragHeader.style.padding = '0 0 8px 0';
    dragHeader.style.boxSizing = 'border-box';
  }

  const contentRow =
    clonedRoot.querySelector('.print-content-row') ||
    clonedRoot.querySelector('.print-left-section')?.parentElement;

  if (contentRow) {
    contentRow.style.display = 'flex';
    contentRow.style.flexDirection = 'column';
    contentRow.style.flexWrap = 'nowrap';
    contentRow.style.alignItems = 'stretch';
    contentRow.style.width = '100%';
    contentRow.style.maxWidth = '100%';
    contentRow.style.gap = `${SECTION_GAP}px`;
    contentRow.style.borderBottom = 'none';
    contentRow.style.overflow = 'visible';
  }

  const leftSection = clonedRoot.querySelector('.print-left-section');
  const rightSection = clonedRoot.querySelector('.print-right-section');

  [leftSection, rightSection].forEach((section) => {
    if (!section) return;
    section.style.flexBasis = '100%';
    section.style.flexShrink = '0';
    section.style.flexGrow = '0';
    section.style.width = '100%';
    section.style.maxWidth = '100%';
    section.style.minWidth = '0';
    section.style.height = 'auto';
    section.style.maxHeight = 'none';
    section.style.overflow = 'visible';
    section.style.alignSelf = 'stretch';
  });

  if (leftSection) {
    leftSection.style.marginBottom = '0';
    leftSection.style.paddingBottom = `${SCORE_CHART_BOTTOM_GAP}px`;
    leftSection.style.borderBottom = '1px solid #ddd';
  }

  if (leftSection && rightSection && rightSection.parentNode) {
    const spacer = clonedRoot.ownerDocument?.createElement('div');
    if (spacer) {
      spacer.style.cssText =
        'width:100%;height:28px;flex-shrink:0;flex-grow:0;display:block;';
      rightSection.parentNode.insertBefore(spacer, rightSection);
    }
  }

  if (rightSection) {
    rightSection.style.paddingTop = '8px';
    rightSection.style.paddingBottom = `${CLONE_BOTTOM_PADDING_PX}px`;
    rightSection.style.borderBottom = 'none';
  }

  clonedRoot
    .querySelectorAll('.print-left-section > div, .print-right-section > div')
    .forEach((inner) => {
      inner.style.gap = '8px';
      inner.style.overflow = 'visible';
    });

  const domainsTitle = rightSection?.querySelector(':scope > div > p');
  if (domainsTitle) {
    domainsTitle.style.marginTop = '4px';
    domainsTitle.style.marginBottom = '6px';
  }

  clonedRoot.querySelectorAll('.print-pdf-chart').forEach((el) => {
    const isDomains = Boolean(el.closest('.print-right-section'));
    const heightPx = isDomains
      ? getDomainsChartHeight(clonedRoot)
      : SCORE_CHART_HEIGHT;
    el.style.height = `${heightPx}px`;
    el.style.minHeight = `${heightPx}px`;
    el.style.maxHeight = `${heightPx}px`;
    el.style.aspectRatio = 'auto';
    el.style.width = '100%';
    el.style.overflow = 'visible';
    el.style.marginBottom = isDomains ? '0' : '0';
    el.style.paddingBottom = isDomains ? '8px' : '0';
    el.style.boxSizing = 'border-box';
  });

  const scoreChart = clonedRoot.querySelector('.print-left-section .print-pdf-chart');
  if (scoreChart) {
    scoreChart.style.marginBottom = '4px';
  }

  clonedRoot.querySelectorAll('.recharts-responsive-container').forEach((el) => {
    const chartWrap = el.closest('.print-pdf-chart');
    const widthPx = chartWrap?.clientWidth || CAPTURE_WIDTH - 48;
    const heightPx =
      chartWrap?.style?.height?.replace('px', '') ||
      chartWrap?.clientHeight ||
      SCORE_CHART_HEIGHT;
    const heightNum = Number(heightPx) || SCORE_CHART_HEIGHT;
    el.style.width = `${widthPx}px`;
    el.style.minWidth = `${widthPx}px`;
    el.style.height = `${heightNum}px`;
    el.style.minHeight = `${heightNum}px`;
    el.style.overflow = 'visible';
  });

  clonedRoot.querySelectorAll('.recharts-wrapper').forEach((el) => {
    el.style.overflow = 'visible';
  });

  clonedRoot.querySelectorAll('.pill-progress').forEach((track) => {
    track.style.width = '100%';
  });
}

function addPdfImageToPage(pdf, canvas, pagePadding, fitSinglePage = true) {
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const contentW = pageW - pagePadding * 2;
  const contentH = pageH - pagePadding * 2;

  const imgW = canvas.width;
  const imgH = canvas.height;
  const widthScale = contentW / imgW;
  const scaledH = imgH * widthScale;

  if (fitSinglePage && scaledH > contentH) {
    const fitScale = contentH / scaledH;
    const drawW = contentW * fitScale;
    const drawH = contentH;
    const drawX = pagePadding + (contentW - drawW) / 2;
    pdf.addImage(
      canvas.toDataURL('image/png'),
      'PNG',
      drawX,
      pagePadding,
      drawW,
      drawH
    );
    return;
  }

  const yOffset = pagePadding + Math.max(0, (contentH - scaledH) / 2);
  pdf.addImage(
    canvas.toDataURL('image/png'),
    'PNG',
    pagePadding,
    yOffset,
    contentW,
    scaledH
  );
}

export function usePrintContentPDF(printRef, options = {}) {
  const {
    fileName = 'teacher-report.pdf',
    orientation = 'portrait',
    fitSinglePage = true,
    includeHeaderButtons = false,
  } = options;

  const [isExporting, setIsExporting] = useState(false);
  const exportingRef = useRef(false);

  const handleDownloadPDF = useCallback(
    async (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();

      if (exportingRef.current) return;
      if (!printRef?.current) {
        console.error('usePrintContentPDF: printRef is not attached.');
        return;
      }

      exportingRef.current = true;
      setIsExporting(true);

      const root = printRef.current;

      try {
        const scale = getScale();

        const canvas = await html2canvas(root, {
          scale,
          useCORS: true,
          allowTaint: false,
          logging: false,
          backgroundColor: '#ffffff',
          scrollX: 0,
          scrollY: 0,
          onclone: (_clonedDoc, clonedElement) => {
            applyPdfCaptureToClone(clonedElement, { includeHeaderButtons });
          },
        });

        const pdf = new jsPDF({
          orientation,
          unit: 'px',
          format: 'a4',
          hotfixes: ['px_scaling'],
        });

        addPdfImageToPage(pdf, canvas, PAGE_PADDING_PX, fitSinglePage);
        pdf.save(fileName);
      } catch (err) {
        console.error('usePrintContentPDF: export failed', err);
        alert('PDF export failed. Please try again.');
      } finally {
        exportingRef.current = false;
        setIsExporting(false);
      }
    },
    [printRef, fileName, orientation, fitSinglePage, includeHeaderButtons]
  );

  return { handleDownloadPDF, isExporting };
}
